import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { slides } from './slides';
export function Deck() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const prefersReducedMotion = useReducedMotion();
  const goToNext = useCallback(() => {
    if (currentIndex < slides.length - 1) {
      setDirection(1);
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex]);
  const goToPrev = useCallback(() => {
    if (currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space' || e.key === 'PageDown') {
        goToNext();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        goToPrev();
      } else if (e.key === 'Home') {
        setDirection(-1);
        setCurrentIndex(0);
      } else if (e.key === 'End') {
        setDirection(1);
        setCurrentIndex(slides.length - 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrev]);
  // Scroll / wheel navigation — one gesture advances exactly one slide.
  //
  // Trackpads emit a long tail of momentum wheel events after a single flick.
  // We fire on the LEADING edge of a gesture, then keep the lock engaged and
  // push the unlock timer further out on EVERY subsequent wheel event, so the
  // lock only releases once scrolling has fully settled (a brief pause). This
  // guarantees one flick = one slide, no matter how much momentum follows.
  useEffect(() => {
    let locked = false;
    let unlockTimer: ReturnType<typeof setTimeout>;
    const scheduleUnlock = () => {
      clearTimeout(unlockTimer);
      unlockTimer = setTimeout(() => {
        locked = false;
      }, 400);
    };
    const handleWheel = (e: WheelEvent) => {
      const delta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      // Ignore tiny/incidental scroll noise.
      if (Math.abs(delta) < 8) return;
      e.preventDefault();
      // While locked, keep deferring the unlock as long as momentum continues,
      // but do NOT advance again.
      if (locked) {
        scheduleUnlock();
        return;
      }
      // Leading edge of a fresh gesture: advance one slide.
      locked = true;
      if (delta > 0) {
        goToNext();
      } else {
        goToPrev();
      }
      scheduleUnlock();
    };
    window.addEventListener('wheel', handleWheel, {
      passive: false
    });
    return () => {
      window.removeEventListener('wheel', handleWheel);
      clearTimeout(unlockTimer);
    };
  }, [goToNext, goToPrev]);
  const CurrentSlide = slides[currentIndex];
  const variants = {
    enter: (dir: number) => ({
      x: prefersReducedMotion ? 0 : dir > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: prefersReducedMotion ? 0 : dir < 0 ? 50 : -50,
      opacity: 0
    })
  };
  return <div className="relative w-screen h-screen overflow-hidden bg-[var(--bg)] flex flex-col" data-id="element-2">
      {/* Slide Content */}
      <div className="flex-1 relative" data-id="element-3">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-4">
          <motion.div key={currentIndex} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
          x: {
            type: 'spring',
            stiffness: 300,
            damping: 30
          },
          opacity: {
            duration: 0.2
          }
        }} className="absolute inset-0 w-full h-full" data-id="element-5">
            <CurrentSlide index={currentIndex} total={slides.length} data-id="element-6" />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Progress rail + counter — one refined element, empty on the first
              slide and full on the last (progress *through* the deck). */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 z-20" data-id="element-7">
        <div className="h-1 w-40 rounded-full bg-[var(--surface-alt)]/70 overflow-hidden" data-id="element-8">
          <motion.div className="h-full rounded-full bg-[var(--accent)]" initial={false} animate={{
          width: `${slides.length > 1 ? currentIndex / (slides.length - 1) * 100 : 100}%`
        }} transition={{
          type: 'spring',
          stiffness: 260,
          damping: 32
        }} data-id="element-9" />
        </div>
        <div className="text-xs font-semibold tracking-widest text-[var(--fg-muted)] tabular-nums" data-id="element-10">
          <span className="text-[var(--fg)]" data-id="element-11">
            {String(currentIndex + 1).padStart(2, '0')}
          </span>
          <span className="mx-1 opacity-40" data-id="element-12">/</span>
          <span data-id="element-13">{String(slides.length).padStart(2, '0')}</span>
        </div>
      </div>

      {/* Invisible click areas for navigation */}
      <div className="absolute top-0 left-0 w-1/4 h-full cursor-w-resize z-10" onClick={goToPrev} data-id="element-14" />
      <div className="absolute top-0 right-0 w-1/4 h-full cursor-e-resize z-10" onClick={goToNext} data-id="element-15" />
    </div>;
}